import React, { useEffect, useMemo, useState } from "react";
import MuiSelect from "../framework/muiSelect";
import { MuiDateTimePicker } from "../framework/muiDateTimePicker";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { FormikProps, useFormik } from "formik";
import { defaultFormikConfig, formikFieldProps } from "../framework/formUtils";
import { useTranslation } from "react-i18next";
import { DayBookingSaveData } from "../../models/dayBooking/dayBookingSaveData";
import * as yup from "yup";
import { addSuccessMsg } from "../../framework/redux/slices/appSlice";
import { useAppDispatch } from "../../framework/redux/hooks";
import { handleApiError } from "../../services/baseService";
import { Base } from "../../framework/common/base";
import { commonBoxStyle, MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { DayBookingType } from "../../models/dayBookingType/dayBookingType";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { submitDayBooking } from "../../framework/redux/slices/dayBookingSlice";
import { ClearButton, SubmitButton } from "../framework/formButtons";
import { DayBookingUsageType } from "../../models/common/enums";
import { AppUtils } from "../../models/common/appUtils";
import { InputProps } from "@mui/material/Input";
import { MuiTimeField } from "../framework/muiTimeField";
import { getDayBookingDuration } from "../../services/dayBookingService";
import { SalaryRowTypeKindCode } from "../../models/salaryRowType/salaryRowType";
import { ICostCenterListItem } from "../../models/costCenter/costCenterListItem";
import { VehicleListItemDto } from "../../models/vehicle/vehicle";

interface IDayBookingFormProps {
    dayBooking: DayBookingSaveData;
    dayBookingTypes: DayBookingType[];
    costCenters: ICostCenterListItem[];
    vehicles: VehicleListItemDto[];
    costCenterId: string | undefined;
    vehicleId: string | undefined;
}

export const DayBookingForm = ({
    dayBooking,
    dayBookingTypes,
    costCenters,
    vehicles,
    costCenterId,
    vehicleId,
}: IDayBookingFormProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { id, date } = useParams();
    const [calculatedDuration, setCalculatedDuration] = useState<number>();
    const [dayBookingType, setDayBookingType] = useState<DayBookingType | null>(null);

    const navigate = useNavigate();
    const handleSubmit = async (formData: DayBookingSaveData) => {
        await dispatch(submitDayBooking(formData))
            .unwrap()
            .then(() => {
                dispatch(
                    addSuccessMsg(t("dayBooking.dayBookingSaveSuccessful"))
                );
                goBack();
            })
            .catch(handleApiError);
    };
    const goBack = () => {
        navigate(generatePath("/worklog/:date?", { date }));
    };

    const formik: FormikProps<DayBookingSaveData> = useFormik({
        ...defaultFormikConfig,
        initialValues: dayBooking,
        onSubmit: handleSubmit,
        validationSchema: yup.object({
            dayBookingTypeId: yup.string().required(
                t("validation.fieldIsRequired", {
                    field: t("dayBooking.dayBookingType"),
                })
            ),
            costCenterId: yup.string().nullable(),
            vehicleId: yup.string().nullable(),
            comment: dayBookingType?.isCommentRequired ? yup.string()
                .required(
                    t("validation.fieldIsRequired", {
                        field: t("common.comment"),
                    })
                )
                .matches(/^(?!\s+$).*/, t("validation.onlyWhitespacesNotAllowed", {
                    field: t("common.comment"),
                }))
                : null,
            day: yup.string().required(
                t("validation.fieldIsRequired", {
                    field: t("time.startTime"),
                })
            ),
            endDate: yup
                .string()
                .test(
                    "required-if-partial-day-booking",
                    t("validation.fieldIsRequired", {
                        field: t("time.endTime"),
                    }),
                    function (value) {
                        return isPartialDayBooking || !!value;
                    }
                )
                .test(
                    "date-validation",
                    t("validation.endTimeMustBeAfterStartTime"),
                    function (value) {
                        const startDate: string = this.resolve(yup.ref("day"));
                        return (
                            isPartialDayBooking ||
                            Base.isSameOrAfter(value, startDate)
                        );
                    }
                ),
        }),
    });

    let allowEntryInDays = dayBookingType?.allowEntryInDays ?? true;
    const allowEntryInHours = dayBookingType?.allowEntryInHours ?? true;

    const measureUnitIsDays = AppUtils.getDayMeasureUnits().some(
        (unit) => unit === dayBookingType?.salaryRowTypeMeasureUnit
    );
    allowEntryInDays = allowEntryInDays || measureUnitIsDays;
    const measureUnitIsHours = AppUtils.getHourMeasureUnits().some(
        (unit) => unit === dayBookingType?.salaryRowTypeMeasureUnit
    );
    const measureUnitIsAmount = AppUtils.getAmountMeasureUnits().some(
        (unit) => unit === dayBookingType?.salaryRowTypeMeasureUnit
    );
    const [optionalDataDisallowed, setOptionalDataDisallowed] = useState<boolean>(false);

    const isPartialDayBooking =
        !measureUnitIsDays &&
        !(
            !allowEntryInHours &&
            allowEntryInDays &&
            !measureUnitIsHours &&
            !measureUnitIsAmount
        );

    const durationFieldLabel = useMemo(
        () =>
            AppUtils.getDurationFieldLabel(
                {
                    measureUnitIsDays,
                    measureUnitIsHours,
                    measureUnitIsAmount,
                },
                isPartialDayBooking
            ),
        [
            measureUnitIsDays,
            measureUnitIsHours,
            measureUnitIsAmount,
            isPartialDayBooking,
        ]
    );

    const showTimeInput =
        measureUnitIsHours || (isPartialDayBooking && !measureUnitIsAmount);

    useEffect(() => {
        setDayBookingType(
            dayBookingTypes?.find(
                (type) => type.id === formik.values.dayBookingTypeId
            )
        );
    }, [dayBookingTypes, formik?.values?.dayBookingTypeId]);

    useEffect(() => {
        if (!showTimeInput) {
            void formik.setFieldValue("amount", Math.floor(formik.values.amount));
        }
    }, [showTimeInput]);

    useEffect(() => {
        void formik.setFieldValue(
            "dayUsageType",
            isPartialDayBooking
                ? DayBookingUsageType.NotFullDay
                : DayBookingUsageType.FullDay
        );
    }, [isPartialDayBooking]);

    useEffect(() => {
        if (!isPartialDayBooking) {
            const days: number =
                Base.dateDiffInDays(
                    new Date(formik.values.day),
                    new Date(formik.values.endDate)
                ) + 1;
            void formik.setFieldValue("amount", days);
        }
    }, [formik.values.day, formik.values.endDate, isPartialDayBooking]);

    const fetchDayBookingDuration = async () => {
        const dbDuration = await getDayBookingDuration({
            dayBookingTypeId: formik.values.dayBookingTypeId ?? "",
            costCenterId: formik.values.costCenterId ?? "",
            vehicleId: formik.values.vehicleId ?? "",
            day: formik.values.day,
            amount:
                Base.dateDiffInDays(formik.values.day, formik.values.endDate) +
                1,
        });

        setCalculatedDuration(dbDuration);
    };

    useEffect(() => {
        if (
            formik.values.day &&
            formik.values.endDate &&
            allowEntryInDays &&
            measureUnitIsDays &&
            dayBookingType?.salaryRowTypeKindCode ===
                SalaryRowTypeKindCode.Vacation.toString()
        ) {
            void fetchDayBookingDuration();
        } else {
            setCalculatedDuration(null);
        }
    }, [
        formik.values.day,
        formik.values.endDate,
        allowEntryInDays,
        measureUnitIsDays,
        dayBookingType?.salaryRowTypeKindCode,
    ]);

    useEffect(() => {
        // Vacations and absences can't have cost center or vehicle
        if (dayBookingType && dayBookingType.salaryRowTypeMeasureUnit !== null &&
            (
                dayBookingType.salaryRowTypeKindCode === SalaryRowTypeKindCode.Absence.toString() ||
                dayBookingType.salaryRowTypeKindCode === SalaryRowTypeKindCode.Vacation.toString()
            )
        ) {
            setOptionalDataDisallowed(true);
            void formik.setFieldValue("costCenterId", null);
            void formik.setFieldValue("vehicleId", null);
        } else {
            setOptionalDataDisallowed(false);
        }
    }, [dayBookingType]);

    // Update costcenter and vehicle based on daybooking type,
    // when creating a new day booking
    useEffect(() => {
        if (!id && !!dayBookingType) {
            if (
                dayBookingType.useWorkShiftCostCenter &&
                !!costCenterId &&
                !formik.values.costCenterId
            ) {
                void formik.setFieldValue(
                    "costCenterId",
                    costCenters.find((c) => c.id === costCenterId)?.id ?? null
                );
            }

            if (
                dayBookingType.useWorkShiftVehicle &&
                !!vehicleId &&
                !formik.values.vehicleId
            ) {
                void formik.setFieldValue(
                    "vehicleId",
                    vehicles.find((c) => c.id === vehicleId)?.id ?? null
                );
            }
        }
    }, [id, dayBookingType, costCenterId, vehicleId]);

    return (
        <MainLayout
            hasPx
            topComponent={
                <PageTitle
                    header={
                        !id
                            ? t("dayBooking.newDayBooking")
                            : t("dayBooking.editDayBooking")
                    }
                />
            }
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                }}
            >
                <MuiSelect
                    label={t("dayBooking.dayBookingType")}
                    required
                    options={(dayBookingTypes || []).map((i) => ({
                        id: i.id,
                        title: i.name,
                    }))}
                    {...formikFieldProps(formik, "dayBookingTypeId")}
                />
                <MuiSelect
                    label={t("workTime.costCenter")}
                    disabled={optionalDataDisallowed}
                    noSelection={true}
                    options={(costCenters || []).map((i) => ({
                        id: i.id,
                        title: i.name,
                        group: i.customerName
                    }))
                        // Sort first so internal cost centers are first.
                        .sort((a, b) => Base.stringCompare(a.group, b.group))
                        .map((c) => ({
                            ...c,
                            // Add label for internal cost centers after sorting.
                            group: c.group || t("workTime.costCenterInternal"),
                        }),
                        )}
                    {...formikFieldProps(formik, "costCenterId")}
                />
                <MuiSelect
                    label={t("common.vehicle")}
                    disabled={optionalDataDisallowed}
                    noSelection={true}
                    options={(vehicles || []).map((i) => ({
                        id: i.id,
                        title: `${i.registerNumber}, ${i.brand}`,
                    }))}
                    {...formikFieldProps(formik, "vehicleId")}
                />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        flexWrap: { sm: "wrap" },
                        gap: "16px",
                    }}
                >
                    <MuiDateTimePicker
                        dateOnly
                        dateLabel={
                            isPartialDayBooking
                                ? t("time.day")
                                : t("time.startDate")
                        }
                        {...formikFieldProps(formik, "day")}
                        onChange={(value) =>
                            void formik.setFieldValue("day", value)
                        }
                        required
                    />
                    {!isPartialDayBooking && (
                        <MuiDateTimePicker
                            dateOnly
                            dateLabel={t("time.endDate")}
                            {...formikFieldProps(formik, "endDate")}
                            onChange={(value) => {
                                void formik.setFieldValue("endDate", value);
                            }}
                            required
                        />
                    )}
                    {showTimeInput ? (
                        <Box sx={{ maxWidth: 100, alignSelf: "flex-end" }}>
                            <MuiTimeField
                                label={durationFieldLabel}
                                {...formikFieldProps(formik, "amount")}
                                value={Base.hoursToTimeStr(
                                    formik.values.amount
                                )}
                                onBlur={(val) =>
                                    void formik.setFieldValue(
                                        "amount",
                                        Base.timeStrToHours(val)
                                    )
                                }
                                required
                            />
                        </Box>
                    ) : (
                        <TextField
                            sx={{ maxWidth: 100, alignSelf: "flex-end" }}
                            label={durationFieldLabel}
                            type="number"
                            required
                            InputProps={
                                {
                                    readOnly: !isPartialDayBooking,
                                } as InputProps
                            }
                            {...formikFieldProps(formik, "amount")}
                            value={
                                calculatedDuration ??
                                Math.floor(formik.values.amount)
                            }
                        />
                    )}
                </Box>
                <TextField
                    name="comment"
                    label={t("common.comment")}
                    required={dayBookingType?.isCommentRequired}
                    multiline
                    minRows={2}
                    fullWidth
                    {...formikFieldProps(formik, "comment")}
                />
                <Box sx={commonBoxStyle}>
                    <ClearButton formik={formik} />
                    <SubmitButton formik={formik} />
                </Box>
            </Box>
        </MainLayout>
    );
};
